import {getCognitoUserTier, getUserTier} from '../../modules/selectors'
import {User as CognitoUser} from '../../types/Cognito'
import {getCookie, removeCookie, setCookie} from '../../../utils/cookies'
import {getDomainName} from '../../../utils/url'
import {UserLastConnectionMethod} from '../../types/Auth'
import {User} from '@auth0/auth0-spa-js'

const SCOPE = 'auth_user_'

/**
 * Generate user cookie config
 * @returns object containing necessary cookie options
 */
export const makeCookieConfiguration = () => ({
  domain: getDomainName(window.location.hostname),
  secure: true
})

/**
 * Cookies config specific for the user
 */
export const USER_COOKIES = {
  tier: `${SCOPE}tier`,
  loginPath: `${SCOPE}login_path`,
  lastConnectionMethod: `${SCOPE}last_connection_method`,
  cognitoTier: `${SCOPE}cognito_tier`
}

/**
 * Save user tier on cookie
 * @param user - from auth0
 */
export const persistUserTierOnCookie = (user: User) => {
  if (!user) return

  const cookieConfiguration = makeCookieConfiguration()

  setCookie(
    USER_COOKIES.tier,
    JSON.stringify(getUserTier(user)),
    cookieConfiguration
  )
}

/**
 * Remove user tier from cookie
 */
export const removeUserTierOnCookie = () => {
  const cookieConfiguration = makeCookieConfiguration()
  removeCookie(USER_COOKIES.tier, cookieConfiguration)
}

/**
 * Get user tier from cookie
 * @returns user tier from auth0
 */
export const getUserTierFromCookie = () => {
  try {
    return (
      (getCookie(USER_COOKIES.tier) &&
        JSON.parse(getCookie(USER_COOKIES.tier))) ||
      []
    )
  } catch {
    return []
  }
}

/**
 * Get the Cognito user tier from cookie
 * @param user - CognitoUser
 */
export const persistCognitoUserTierOnCookie = (user: CognitoUser) => {
  if (!user) return

  const cookieConfiguration = makeCookieConfiguration()

  setCookie(
    USER_COOKIES.cognitoTier,
    JSON.stringify(getCognitoUserTier(user)),
    cookieConfiguration
  )
}

export const getCognitoUserTierFromCookie = () => {
  try {
    const tier = getCookie(USER_COOKIES.cognitoTier)
    return (tier && JSON.parse(tier)) || []
  } catch {
    return []
  }
}

export const removeCognitoUserTierFromCookie = () => {
  const cookieConfiguration = makeCookieConfiguration()
  removeCookie(USER_COOKIES.cognitoTier, cookieConfiguration)
}

/**
 * Save the last login url, usually from auth0
 * @param searchURI - url to be saved
 */
export const setLastLoginUrl = (searchURI: string) => {
  const cookieConfiguration = makeCookieConfiguration()
  setCookie(USER_COOKIES.loginPath, searchURI, cookieConfiguration)
}

/**
 * Get the last login url
 * @returns user last login url
 */
export const getLastLoginUrl = (): string => getCookie(USER_COOKIES.loginPath)

/**
 * Remove the last login url
 */
export const removeLastLoginUrl = () => {
  const cookieConfiguration = makeCookieConfiguration()
  removeCookie(USER_COOKIES.loginPath, cookieConfiguration)
}

/**
 * Save user last used connection method on cookie
 * @param lastConnectionMethod - sms|whatsapp|facebook|google-oauth2
 */
export const persistUserLastConnectionMethodOnCookie = (
  lastConnectionMethod: UserLastConnectionMethod
) => {
  if (!lastConnectionMethod) return

  const cookieConfiguration = makeCookieConfiguration()

  setCookie(
    USER_COOKIES.lastConnectionMethod,
    lastConnectionMethod,
    cookieConfiguration
  )
}

/**
 * Remove user last used connection method
 */
export const removeUserLastConnectionMethodCookie = () => {
  const cookieConfiguration = makeCookieConfiguration()
  removeCookie(USER_COOKIES.lastConnectionMethod, cookieConfiguration)
}

/**
 * Get user last used connection method
 * @returns user last used connection method (sms|whatsapp|facebook|google-oauth2)
 */
export const getUserLastConnectionMethodFromCookie = () =>
  getCookie(USER_COOKIES.lastConnectionMethod) as UserLastConnectionMethod
